import { zodResolver } from "@hookform/resolvers/zod";
import { KikMarketingCloudDoubleOptInProcedures } from "@kikocosmeticsorg/uc-api-nest-common-fe";
import { KikUserZodSchemas } from "@kikocosmeticsorg/uc-api-nest-common-fe/zod";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";

import ActionCodeDialog from "~/components/common/action-code-dialog";
import Button from "~/components/common/button";
import LinkWrapper from "~/components/common/link-wrapper";
import Errors from "~/components/form/errors";
import InputField from "~/components/form/input-field";
import { useActionCodeContext } from "~/contexts/action-code";
import { useAuthContext } from "~/contexts/auth";
import { useFormTranslations } from "~/hooks/use-form-translations";
import { useGlobalNotification } from "~/hooks/use-global-notification";
import Logger from "~/services/logger/logger";
import appRoutes from "~/utils/app-routes";
import Constants from "~/utils/constants";
import crossCountryUtils from "~/utils/cross-country-utils";

import styles from "./guest-track-your-order.module.scss";

const formSchema = z.object({
  orderId: z
    .string({
      required_error: "error.missing_required_field",
      invalid_type_error: "error.missing_required_field",
    })
    .trim()
    .min(1, "error.missing_required_field")
    .min(8, "error.invalid_oder_id")
    .regex(/^[A-Za-z0-9]+$/, "error.invalid_alphanumeric"),
  email: KikUserZodSchemas.email,
});

type FormData = z.infer<typeof formSchema>;

type Props = {
  mode?: "track" | "return";
  onNext: () => void;
};

type GuestTrackYourOrderMode = NonNullable<Props["mode"]>;

const COPY_BY_MODE = {
  return: {
    titleKey: "generic.requestReturn",
    descriptionKey: "generic.requestReturnDesc",
    submitLabelKey: "generic.continue",
  },
  track: {
    titleKey: "generic.track",
    descriptionKey: "generic.trackdesc",
    submitLabelKey: "generic.form.tracksubmit",
  },
} as const satisfies Record<
  GuestTrackYourOrderMode,
  {
    titleKey: string;
    descriptionKey: string;
    submitLabelKey: string;
  }
>;

export default function GuestTrackYourOrderStep1({ mode = "track", onNext }: Props) {
  const t = useTranslations();
  const router = useRouter();
  const {
    timeLeft,
    resetTimer,
    dialogOpenState: [, setIsActionCodeDialogOpen],
  } = useActionCodeContext();
  const { sendEmailVerification, isLoading, authError, guestTrackOrderAuthWithActionCode } = useAuthContext();
  const { newErrorGlobalNotification } = useGlobalNotification();
  const currentCountry = crossCountryUtils.getCurrentCountryCode(router);
  const currentLanguage = crossCountryUtils.getCurrentLanguageCode(router);
  const [filledData, setFilledData] = useState<FormData>({} as FormData);
  const { getFieldError } = useFormTranslations();

  const {
    register,
    handleSubmit,
    clearErrors,
    formState: { errors, isValid },
    getValues,
  } = useForm<FormData>({
    ...Constants.USE_FORM_VALIDATION_DEFAULTS,
    resolver: zodResolver(formSchema),
  });

  const doAuthenticateWithActionCode = (actionCode: string) => {
    const data = formSchema.parse(filledData);

    return guestTrackOrderAuthWithActionCode({
      actionCode,
      email: data.email,
      orderId: data.orderId,
    })
      .finally(() => setIsActionCodeDialogOpen(!1))
      .then((response) => {
        Logger.instance.debug("GuestTrackYourOrderStep1: got response", response);
        onNext();
      })
      .catch(() => {
        Logger.instance.warn("GuestTrackYourOrderStep1: error", authError);
        const errorMessage = authError?.messages?.[0] || "generic.error";
        newErrorGlobalNotification(
          {
            title: "generic.error",
            description: t(errorMessage),
          },
          { timeout: 7500 }
        );
      });
  };

  const doSendEmailVerification = (data: FormData = getValues()) => {
    const parsed = formSchema.safeParse(data);
    if (!parsed.success) {
      return Promise.resolve();
    }

    if ((timeLeft || 0) > 0) {
      setIsActionCodeDialogOpen(true);
      return Promise.resolve();
    }

    return sendEmailVerification({
      EmailAddress: parsed.data.email,
      CountryID: currentCountry,
      LanguageID: currentLanguage,
      procedure: KikMarketingCloudDoubleOptInProcedures.TRACK_YOUR_ORDER,
    })
      .then(() => {
        resetTimer();
        Logger.instance.info("GuestTrackYourOrderStep1: sent email");
        setIsActionCodeDialogOpen(true);
      })
      .catch((e) => {
        setIsActionCodeDialogOpen(false);
        Logger.instance.error("GuestTrackYourOrderStep1: could not call sendEmailVerification", e);
      });
  };

  const onSubmit = (data: FormData) => {
    setFilledData(data);

    return doSendEmailVerification(data);
  };

  const copy = COPY_BY_MODE[mode];

  return (
    <div className={styles.trackBox}>
      <h1 className={styles.title}>{t(copy.titleKey)}</h1>
      <p>{t(copy.descriptionKey)}</p>
      <p>
        {t.rich("generic.trackdesc2", {
          link: (content) => <LinkWrapper href={appRoutes.MYACCOUNT}>{content}</LinkWrapper>,
        })}
      </p>
      <form
        className={styles.form}
        onSubmit={handleSubmit(onSubmit, (data) =>
          Logger.instance.warn("GuestTrackYourOrderStep1: invalid form", data)
        )}
      >
        <div className={styles.inputs}>
          <InputField
            {...register("orderId")}
            type="text"
            label={t("generic.orderN")}
            error={getFieldError(errors.orderId?.message, {
              fieldName: "",
            })}
            onClearErrors={() => clearErrors("orderId")}
            customStyles={{ inputField: styles.initialAppeareance }}
          />
          <InputField
            {...register("email")}
            type="email"
            label={t("generic.form.email_label")}
            error={getFieldError(errors.email?.message, {
              fieldName: t("generic.form.email_label"),
              minLength: KikUserZodSchemas.emailMinlength,
            })}
            onClearErrors={() => clearErrors("email")}
            customStyles={{ inputField: styles.initialAppeareance }}
          />
        </div>
        <p className={styles.mandatoryLabel}>{t("generic.form.mandatory_long")}</p>
        <Errors error={authError}></Errors>
        <Button type="submit" variant="primary" disabled={!isValid} loading={isLoading || !router.isReady}>
          {t(copy.submitLabelKey)}
        </Button>
      </form>
      <ActionCodeDialog
        onSubmit={doAuthenticateWithActionCode}
        onSendAnother={() => doSendEmailVerification()}
        email={filledData.email}
      />
    </div>
  );
}
